import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";
import { useAuth } from "@clerk/clerk-react";

export type AIJobStatus = "queued" | "running" | "complete" | "failed";

export interface AIJob<TOutput = unknown> {
  id: string;
  projectId: string;
  kind: string;
  status: AIJobStatus;
  model: string | null;
  promptTokens: number | null;
  completionTokens: number | null;
  costUsd: number | null;
  output: TOutput | null;
  error: string | null;
  createdAt: string;
  completedAt: string | null;
}

/**
 * Poll a job until it reaches a terminal state. Use for non-streaming jobs
 * (parse_summary, parse_matrix, briefs, qa). For streaming jobs use
 * `useAiJobStream` instead.
 */
export function useAiJob<TOutput = unknown>(jobId: string | null, opts?: { intervalMs?: number }) {
  const interval = opts?.intervalMs ?? 1500;
  return useQuery<AIJob<TOutput>>({
    queryKey: jobId ? ["aiJob", jobId] : ["aiJob", null],
    enabled: Boolean(jobId),
    queryFn: () => customFetch<AIJob<TOutput>>(`/api/v1/jobs/${jobId}`),
    // Stop polling once terminal.
    refetchInterval: (q) => {
      const data = q.state.data as AIJob<TOutput> | undefined;
      if (!data) return interval;
      return data.status === "complete" || data.status === "failed" ? false : interval;
    },
  });
}

// ---------------------------------------------------------------------------
// useAiJobStream — fetch-based SSE so we can attach the Clerk JWT.
// EventSource doesn't support custom headers, hence the manual reader.
// ---------------------------------------------------------------------------

interface StreamState {
  text: string;
  status: "connecting" | "streaming" | "done" | "error";
  error: string | null;
}

interface StreamEvent {
  delta?: string;
  done?: true;
  error?: string;
  output?: unknown;
}

export function useAiJobStream(jobId: string | null) {
  const { getToken } = useAuth();
  const [state, setState] = useState<StreamState>({
    text: "",
    status: "connecting",
    error: null,
  });
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!jobId) return;

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setState({ text: "", status: "connecting", error: null });

    void (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`/api/v1/jobs/${jobId}/stream`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: ctrl.signal,
        });
        if (!res.ok || !res.body) {
          setState({
            text: "",
            status: "error",
            error: `Stream failed: ${res.status} ${res.statusText}`,
          });
          return;
        }

        setState((s) => ({ ...s, status: "streaming" }));

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // SSE frames are separated by blank lines.
          let idx: number;
          while ((idx = buffer.indexOf("\n\n")) >= 0) {
            const frame = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            for (const line of frame.split("\n")) {
              if (!line.startsWith("data:")) continue;
              const json = line.slice(5).trim();
              if (!json) continue;
              try {
                const event = JSON.parse(json) as StreamEvent;
                if (event.delta) {
                  setState((s) => ({ ...s, text: s.text + event.delta! }));
                } else if (event.done) {
                  setState((s) => ({ ...s, status: "done" }));
                } else if (event.error) {
                  setState({ text: "", status: "error", error: event.error });
                }
              } catch {
                // Ignore malformed frames; the heartbeat ": ping" lines come
                // here too and are fine to drop.
              }
            }
          }
        }
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        setState({
          text: "",
          status: "error",
          error: (err as Error).message,
        });
      }
    })();

    return () => {
      ctrl.abort();
    };
  }, [jobId, getToken]);

  return {
    ...state,
    abort: () => abortRef.current?.abort(),
  };
}
